import { ACK_NOTE_MAX, MODULE_ALARM_TYPES, isChecklistAlarmType, isGeofenceAlarmType, type Alarm, type AlarmNotification } from "@fts/core";

const isModuleAlarmType = (type: string) => (MODULE_ALARM_TYPES as readonly string[]).includes(type);
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { FormError } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { EmptyState, Note, PageHeader, Panel } from "../components/ui";
import type { PersonName } from "../data/provider";
import { useI18n } from "../i18n";
import { alarmTitle, canAcknowledge, canAcknowledgeAlarm, formatAlarmTime, formatClock, personName } from "../lib/alarms";
import { errorCode } from "../lib/errors";
import { sitePath } from "../lib/fleet";
import { useAlarmCount, useAlertPeople } from "../state/alarms";
import { useSession, type TenantData } from "../state/session";
import { AlarmGeofencePanel } from "./AlarmGeofence";
import { AlarmChecklistPanel } from "./ChecklistParts";
import { AlarmBadges, AlarmStatusBadge } from "./AlarmParts";
import { operatorName } from "./Alarms";

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="dl-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

type Loaded = {
  alarm: Alarm | null;
  notifications: AlarmNotification[];
  /** alarm_people(id): names of the recipients and the acknowledger, for every role that sees the alarm. */
  names: PersonName[];
  error: boolean;
  loaded: boolean;
};
const EMPTY: Loaded = { alarm: null, notifications: [], names: [], error: false, loaded: false };

export function AlarmDetail({ data, alarmId }: { data: TenantData; alarmId: string }) {
  const { t, locale } = useI18n();
  const { provider, tenant, role, user } = useSession();
  const { refresh: refreshCount } = useAlarmCount();
  const { people } = useAlertPeople();
  const [state, setState] = useState<Loaded>(EMPTY);
  const [justAcked, setJustAcked] = useState(false);
  const tenantId = tenant?.id;
  const known = useMemo<PersonName[]>(() => [...people, ...state.names.filter((n) => !people.some((p) => p.userId === n.userId))], [people, state.names]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [alarm, notifications, names] = await Promise.all([
        provider.getAlarm(tenantId, alarmId),
        provider.listAlarmNotifications(alarmId),
        // Names are a nicety: without them the rows fall back to the neutral label.
        provider.listAlarmPeople(alarmId).catch(() => [] as PersonName[]),
      ]);
      setState({ alarm, notifications, names, error: false, loaded: true });
    } catch {
      setState((s) => ({ ...s, error: true, loaded: true }));
    }
  }, [provider, tenantId, alarmId]);

  useEffect(() => {
    setState(EMPTY);
    setJustAcked(false);
    void load();
  }, [load]);

  const back = (
    <a href="#/alarms" className="button button-secondary button-sm">
      <Icon name="arrowLeft" size={15} />
      {t("alarmDetail.back")}
    </a>
  );

  if (!state.loaded)
    return (
      <div className="loading" role="status">
        <span className="spinner" aria-hidden="true" />
        {t("common.loading")}
      </div>
    );
  if (state.error && !state.alarm) return <EmptyState icon="alert" title={t("alarms.loadError")} action={back} />;
  const a = state.alarm;
  if (!a) return <EmptyState icon="bell" title={t("alarmDetail.notFound")} action={back}>{t("alarmDetail.notFoundHint")}</EmptyState>;

  const dash = t("common.empty");
  const vehicle = a.vehicleId ? data.vehicles.find((v) => v.id === a.vehicleId) : undefined;
  const site = a.siteId ? data.sites.find((s) => s.id === a.siteId) : undefined;
  const sitePathLabel = site ? sitePath(data.sites, site.id).map((s) => s.name).join(" / ") : dash;
  const title = alarmTitle(t, a);
  const time = (iso: string | null) => formatAlarmTime(iso, locale, true) ?? dash;
  // A site-scoped user cannot acknowledge an alarm whose site was deleted (acknowledge_alarm rule).
  const mayAck = tenantId ? canAcknowledgeAlarm(user, tenantId, role, a) : false;
  const seesAll = canAcknowledge(role); // safety+ see every notification row of their sites (RLS)

  return (
    <>
      <PageHeader
        eyebrow={<AlarmStatusBadge status={a.status} />}
        title={t("alarmDetail.heading", { type: title, vehicle: vehicle?.fleetNo ?? dash })}
        subtitle={`${time(a.occurredAt)} · ${t("alarmDetail.timeZoneNote")}`}
        actions={back}
      />

      {a.type === "impact" && <Note>{t("alarm.noBlock")}</Note>}
      {isModuleAlarmType(a.type) && <Note>{t("modules.alarmNoBlock")}</Note>}
      {a.notifyError === "no_recipients" ? (
        <p className="warning-banner tone-crit" role="alert">
          <Icon name="alert" size={16} />
          <span>{t("alarmDetail.noRecipients")}</span>
        </p>
      ) : (
        a.notifyError && (
          <p className="warning-banner" role="alert">
            {t("alarmDetail.notifyError")}
          </p>
        )
      )}

      <div className="detail-grid">
        <Panel title={t("alarmDetail.facts")}>
          <dl className="dl">
            <Fact label={t("alarmDetail.type")}>
              {title}
              <AlarmBadges alarm={a} />
            </Fact>
            <Fact label={t("alarmDetail.occurredAt")}>{a.timeUnknown ? <span className="muted">{t("alarm.timeUnknown")}</span> : time(a.occurredAt)}</Fact>
            <Fact label={t("alarmDetail.receivedAt")}>{time(a.receivedAt)}</Fact>
            {a.timeUnknown ? (
              <Fact label={t("alarm.badge.timeUnknown")}>{t("alarmDetail.timeUnknownNote")}</Fact>
            ) : (
              a.delayed && <Fact label={t("alarm.badge.delayed")}>{t("alarmDetail.delayedNote", { time: time(a.occurredAt), received: time(a.receivedAt) })}</Fact>
            )}
            {a.repeatCount > 0 && (
              <Fact label={t("alarmDetail.repeats")}>
                {a.lastOccurredAt
                  ? t("alarm.badge.repeat", { count: a.repeatCount, time: formatClock(new Date(a.lastOccurredAt), locale) })
                  : t("alarm.badge.repeatNoTime", { count: a.repeatCount })}
                <span className="field-hint">{t("alarm.badge.repeatHint")}</span>
              </Fact>
            )}
            <Fact label={t("alarmDetail.workTime")}>{a.offHours ? t("alarmDetail.offHours") : t("alarmDetail.inHours")}</Fact>
            <Fact label={t("alarmDetail.vehicle")}>
              {vehicle ? (
                <a href={`#/vehicles/${encodeURIComponent(vehicle.id)}`} className="subtle-link mono">
                  {vehicle.fleetNo}
                </a>
              ) : (
                dash
              )}
            </Fact>
            <Fact label={t("alarmDetail.site")}>
              {site ? (
                <a href={`#/sites/${encodeURIComponent(site.id)}`} className="subtle-link">
                  {sitePathLabel}
                </a>
              ) : (
                dash
              )}
            </Fact>
            <Fact label={t("alarmDetail.device")}>{a.deviceId ? <span className="mono">{a.deviceId}</span> : dash}</Fact>
            <Fact label={t("alarmDetail.operator")}>
              {a.operatorId ? (operatorName(data, a.operatorId) ?? dash) : <span className="muted">{t("alarmDetail.noOperator")}</span>}
            </Fact>
          </dl>
        </Panel>

        <Panel title={t("alarmAck.title")}>
          {a.status === "acknowledged" ? (
            <>
              {justAcked && (
                <p className="success-banner" role="status">
                  {t("alarmAck.done")}
                </p>
              )}
              <dl className="dl">
                <Fact label={t("alarmAck.by")}>{personName(t, known, a.ackedBy, user?.id)}</Fact>
                <Fact label={t("alarmAck.at")}>{time(a.ackedAt)}</Fact>
                <Fact label={t("alarmAck.note")}>{a.ackNote ? <span className="pre-wrap">{a.ackNote}</span> : <span className="muted">{t("alarmAck.noNote")}</span>}</Fact>
              </dl>
            </>
          ) : mayAck ? (
            <AckForm
              alarmId={a.id}
              onDone={async () => {
                setJustAcked(true);
                await load();
                await refreshCount();
              }}
            />
          ) : (
            <p className="panel-text">
              {t("alarmAck.waiting")} {t("alarmAck.needsRole")}
            </p>
          )}
        </Panel>
      </div>

      {isGeofenceAlarmType(a.type) && <AlarmGeofencePanel alarm={a} />}
      {isChecklistAlarmType(a.type) && <AlarmChecklistPanel alarm={a} />}

      <Panel title={t("alarmNotify.title")} flush>
        {state.notifications.length === 0 ? (
          <p className="table-empty">{t("alarmNotify.none")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("alarmNotify.recipient")}</th>
                  <th scope="col" className="col-hide-sm">
                    {t("alarmNotify.channel")}
                  </th>
                  <th scope="col">{t("alarmNotify.kind")}</th>
                  <th scope="col">{t("alarmNotify.status")}</th>
                  <th scope="col" className="col-hide-sm">
                    {t("alarmNotify.sentAt")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.notifications.map((n) => (
                  <tr key={n.id}>
                    <td>{n.userId ? personName(t, known, n.userId, user?.id) : t("alarmNotify.external")}</td>
                    <td className="col-hide-sm">{t(`alarmChannel.${n.channel}`)}</td>
                    <td>{t(`alarmKind.${n.kind}`)}</td>
                    <td>
                      <span className={`badge tone-${n.status === "sent" ? "ok" : n.status === "failed" ? "crit" : "neutral"}`}>{t(`alarmNStatus.${n.status}`)}</span>
                    </td>
                    <td className="col-hide-sm nowrap">
                      {n.sentAt ? time(n.sentAt) : n.dueAt && (n.status === "pending" || n.status === "sending") ? t("alarmNotify.dueAt", { time: time(n.dueAt) }) : dash}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!seesAll && <p className="panel-footnote">{t("alarmNotify.ownOnly")}</p>}
      </Panel>
    </>
  );
}

function AckForm({ alarmId, onDone }: { alarmId: string; onDone: () => Promise<void> }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const tooLong = note.trim().length > ACK_NOTE_MAX;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (tooLong || busy) return;
    setBusy(true);
    setError(null);
    try {
      await provider.acknowledgeAlarm(alarmId, note.trim() || null);
      await onDone();
    } catch (err) {
      // Someone else acknowledged it meanwhile: show their acknowledgement instead of an error.
      if (errorCode(err) === "already_acknowledged") {
        await onDone();
        return;
      }
      setError(err);
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
      <p className="panel-text">{t("alarmAck.waiting")}</p>
      <div className="field">
        <label htmlFor="ack-note">
          {t("alarmAck.noteLabel")}
          <span className="field-optional"> · {t("form.optional")}</span>
        </label>
        <textarea
          id="ack-note"
          rows={3}
          value={note}
          aria-invalid={tooLong || undefined}
          aria-describedby={tooLong ? "ack-note-hint ack-note-error" : "ack-note-hint"}
          onChange={(e) => setNote(e.target.value)}
        />
        <p id="ack-note-hint" className="field-hint">
          {t("alarmAck.noteHint")}
        </p>
        {tooLong && (
          <p id="ack-note-error" className="field-error">
            {t("alarmAck.tooLong")}
          </p>
        )}
      </div>
      {error !== null && <FormError error={error} />}
      <div>
        <button type="submit" className="button button-primary" disabled={busy || tooLong}>
          <Icon name="check" size={16} />
          {busy ? t("common.working") : t("alarmAck.button")}
        </button>
      </div>
    </form>
  );
}
